import { useEffect, useMemo, useRef, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability, useDeskOperator } from '@lemontaigu/platform-ui';
import {
  ARRHES_METHOD_LABELS_FR,
  confirmationStatusFr,
  formatIban,
  renderTerms,
  transferInstructionLines,
  type BankTransferDetails,
  type BedConfig,
  type ConfirmationMail,
  type DeskSale,
  type GuestLang,
  type GuestSummary,
  type Product,
  type RatePlan,
  type Reservation,
  type Room,
  type RoomStayView,
  type RoomType,
  type StayRestriction,
  type StoredCancellationPolicy,
  type TermsRules,
  type TermsText,
} from '@lemontaigu/pms-core';
import type { PmsServices } from '@lemontaigu/pms-adapters';
import { fmtAmount, fmtDate, outlookKind, parseEurosToCents } from '../logic.js';
import { GuestPicker } from '../GuestPicker.js';
import { BathIcon, BedIcons, OutlookIcon, WcIcon } from '../RoomIcons.js';
import {
  AFTER_YES,
  DEFAULT_DEPOSIT,
  STEP_LABEL,
  addRoom,
  agesMissing,
  allRooms,
  bedChoices,
  bookingParty,
  categoryRooms,
  bookingRef,
  buildCallCommand,
  buildTurnaway,
  byCategoryOrder,
  checklist,
  confirmationText,
  confirmationMail,
  datesReadback,
  deskPolicies,
  pickedPolicy,
  policyRules,
  transferDue,
  departureOf,
  depositCents,
  emailOk,
  emptyCall,
  extraProducts,
  extrasTotal,
  freeRoomIds,
  greetingFr,
  heldElsewhere,
  laneSteps,
  offerQuery,
  partyFr,
  planShort,
  recapText,
  removeRoom,
  roomAssignments,
  roomFacts,
  roomsFitting,
  roomsTax,
  roomsTotal,
  speakEmail,
  spellOut,
  stayTotal,
  switchRoom,
  termsFor,
  type CallCatalogue,
  type CallDraft,
  type CallFigures,
  type CallRoom,
  type CallStep,
  type SoldSale,
} from './callModel.js';

// « Réservation manuelle » (PB5, PMS-PHONE-BOOKING-BRIEF §2; renamed from « Appel »
// on 2026-10-27, since a booking also arrives by e-mail): the prototype's
// screens through the ports — the desk's offer (PB3), the manual command (PB4),
// the anonymous enquiry (PB9). The call guide on the left, the booking card
// filling in on the right. Every step can be reached in any order, except those
// after the fork, which open on « oui ».

const LANGS: { v: GuestLang; label: string }[] = [
  { v: 'fr', label: 'Français' },
  { v: 'en', label: 'English' },
  { v: 'es', label: 'Español' },
  { v: 'de', label: 'Deutsch' },
];
const OCCASIONS = ['Anniversaire', 'Anniversaire de mariage', 'Voyage de noces', 'Déplacement professionnel'];
const REASON_FR: Record<string, string> = {
  not_on_sale: 'pas de tarif publié',
  too_many_guests: 'trop petite pour le groupe',
  hotel_closed: 'hôtel fermé',
  no_rate: 'pas de tarif',
  closed: 'fermée à la vente',
  min_stay: 'séjour minimum',
  closed_to_arrival: 'arrivée interdite',
  closed_to_departure: 'départ interdit',
  max_stay: 'séjour maximum',
  full: 'complet',
  unknown_category: 'catégorie inconnue',
  unknown_plan: 'plan inconnu',
};

/** Every figure here is in euros: no currency sign on screen (operator, 2026-10-27). */
const amt = fmtAmount;
/** A line from the shared terms renderer, without its sign. */
const noSign = (line: string) => line.replace(/\s?€/g, '');

/** « Script » (« Texte à dire » until 2026-10-27) — the full sentences, or only the key points (the default). */
const SCRIPT_KEY = 'pms.call.showScript';
function readShowScript(): boolean {
  try {
    return window.localStorage.getItem(SCRIPT_KEY) === '1';
  } catch {
    return false;
  }
}
function writeShowScript(on: boolean) {
  try {
    window.localStorage.setItem(SCRIPT_KEY, on ? '1' : '0');
  } catch {
    // A private window: the choice lasts the page.
  }
}

const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

export interface CallFlowProps {
  propertyCode: string;
  services: PmsServices;
  /** The bookings Réservations already holds — a returning guest's history.
   *  Omitted (Planning, which reads only the window shown): the flow reads them itself. */
  reservations?: readonly Reservation[];
  onClose: () => void;
  /** A booking was saved: the list reloads. */
  onSaved: () => void;
}

export function CallFlow({ propertyCode, services, reservations: given, onClose, onSaved }: CallFlowProps) {
  const canNeeds = useCapability(CAPABILITIES.PMS_NEEDS_READ);
  const [read, setRead] = useState<readonly Reservation[]>([]);
  useEffect(() => {
    if (given !== undefined) return;
    void services.reservations.list().then((r) => {
      if (r.ok) setRead(r.value.filter((x) => x.propertyCode === propertyCode));
    });
  }, [given, services, propertyCode]);
  const reservations = given ?? read;
  const [call, setCall] = useState<CallDraft>(() => emptyCall(propertyCode));
  const [step, setStep] = useState<CallStep>('accueil');
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [units, setUnits] = useState<Room[]>([]);
  const [bedConfigs, setBedConfigs] = useState<BedConfig[]>([]);
  // The logements on a room over the stay asked: which rooms are still free.
  const [stays, setStays] = useState<RoomStayView[] | null>(null);
  const [showScript, setShowScript] = useState(readShowScript);
  const [options, setOptions] = useState<DeskSale[]>([]);
  // Which party the options were read for: another room's options are never its price.
  const [optionsFor, setOptionsFor] = useState('');
  const [optionsError, setOptionsError] = useState('');
  const [terms, setTerms] = useState<TermsText | null>(null);
  // The kept plan's own rules: the deposit default, and what a relaxation starts from.
  const [planTerms, setPlanTerms] = useState<TermsRules | null>(null);
  // PB10: the property's policies (the desk picks one) and its bank-transfer instructions.
  // `bank` undefined = not read yet; null = the property has none set.
  const [policies, setPolicies] = useState<StoredCancellationPolicy[]>([]);
  const [bank, setBank] = useState<BankTransferDetails | null | undefined>(undefined);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<{ ref: string; id: string } | null>(null);
  // The rooms given at the call, once assigned after the save — or why one was not.
  const [assigned, setAssigned] = useState<{ given: string[]; failed: string[] }>({ given: [], failed: [] });
  // M3: the confirmation the platform sends — prepared at « Enregistrer ».
  const desk = useDeskOperator();
  const [mail, setMail] = useState<ConfirmationMail | null>(null);
  const [mailError, setMailError] = useState('');
  const [cardLink, setCardLink] = useState('');
  const [mailBusy, setMailBusy] = useState(false);
  const [counted, setCounted] = useState('');
  const today = useMemo(() => todayIsoParis(), []);
  const set = (patch: Partial<CallDraft>) => setCall((c) => ({ ...c, ...patch, confirmed: patch.confirmed ?? false }));

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    void Promise.all([services.reference.roomTypes(), services.reference.ratePlans(), services.reference.products()]).then(([t, p, x]) => {
      if (t.ok) setRoomTypes(t.value.filter((r) => r.propertyCode === propertyCode));
      if (p.ok) setRatePlans(p.value.filter((r) => r.propertyCode === propertyCode));
      if (x.ok) setProducts(x.value.filter((r) => r.propertyCode === propertyCode));
      const failed = [t, p, x].find((r) => !r.ok);
      if (failed && !failed.ok) setError(failed.error.message);
    });
    // The rooms and their arrangements: what the bed and preference choices offer.
    // Unreadable, the choices are simply not offered — the booking still goes through.
    void Promise.all([services.reference.rooms(), services.reference.bedConfigs()]).then(([r, b]) => {
      if (r.ok) setUnits(r.value.filter((x) => x.propertyCode === propertyCode));
      if (b.ok) setBedConfigs(b.value);
    });
  }, [services, propertyCode]);

  // The options for the stay and party: the website's prices and lodging (PB3).
  const query = offerQuery(call);
  const queryKey = query ? JSON.stringify(query) : '';
  useEffect(() => {
    if (!query) {
      setOptions([]);
      return;
    }
    let live = true;
    void services.deskOffer.options(query).then((r) => {
      if (!live) return;
      setOptionsError(r.ok ? '' : r.error.message);
      setOptions(r.ok ? r.value : []);
      setOptionsFor(queryKey);
    });
    return () => {
      live = false;
    };
    // queryKey stands for query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, queryKey]);

  useEffect(() => {
    if (!query) {
      setStays(null);
      return;
    }
    let live = true;
    void services.reservations.rackWindow(query.from, query.to).then((r) => {
      if (live) setStays(r.ok ? r.value.stays : null);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, query?.from, query?.to]);

  const sale: SoldSale | null = useMemo(() => {
    if (!call.pick || optionsFor !== queryKey) {
      // While this room's options load, the price it was left with stands.
      const kept = call.rooms[call.active];
      return kept && kept.pick === call.pick && kept.adults === call.adults && kept.childAges === call.childAges ? kept.sale : null;
    }
    const o = options.find((x) => x.category === call.pick!.category && x.plan === call.pick!.plan);
    return o && o.sellable ? o : null;
  }, [options, optionsFor, queryKey, call.pick, call.rooms, call.active, call.adults, call.childAges]);

  // The policies the desk may pick from, and the transfer instructions: read once per property.
  useEffect(() => {
    let live = true;
    void services.cancellationPolicies.policies().then((r) => {
      if (live) setPolicies(r.ok ? r.value : []);
    });
    void services.bankTransfer.details().then((r) => {
      if (live) setBank(r.ok ? r.value : null);
    });
    return () => {
      live = false;
    };
  }, [services]);

  // The kept option's terms, rendered for this stay by the one renderer (P11).
  useEffect(() => {
    if (!query || !call.pick) {
      setTerms(null);
      setPlanTerms(null);
      return;
    }
    let live = true;
    void services.deskOffer.quote({ ...query, category: call.pick.category, plan: call.pick.plan }).then((r) => {
      if (!live) return;
      setTerms(r.ok && 'terms' in r.value ? r.value.terms : null);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, queryKey, call.pick?.category, call.pick?.plan]);

  const catalogue: CallCatalogue = { roomTypes, ratePlans, products, canNeeds, policies, bank: bank ?? null, today };
  const steps = laneSteps(call.lane);
  const booking = call.decision === 'book';
  const label = (code: string) => roomTypes.find((t) => t.code === code)?.labelFr ?? code;
  const planLabel = (code: string) => ratePlans.find((p) => p.code === code)?.labelFr ?? code;
  // This room's sleepers (the bed choice); the booking's, for the per-person extras.
  const people = call.adults + call.childAges.filter((a) => a === null || a >= 3).length;
  const party = bookingParty(call);
  const bookingPeople = party.adults + party.childAges.filter((a) => a === null || a >= 3).length;
  const rooms = allRooms(call, sale);
  const multi = rooms.length > 1;
  // This room's price (the grid, the negotiated price); the booking's, every room together.
  const roomStayCents = sale ? stayTotal(sale, call.overrideCents) : 0;
  const stayCents = roomsTotal(rooms);
  const taxCents = roomsTax(rooms);
  const extrasCents = extrasTotal(call, products, bookingPeople);
  // The rules the booking starts from: the policy the desk picked, else the one the plan follows.
  const picked = pickedPolicy(call, policies);
  const baseTerms = picked !== null ? policyRules(picked) : planTerms;
  const effectiveTerms = termsFor(call, baseTerms);
  const deposit = call.deposit ?? DEFAULT_DEPOSIT;
  const depositValue = depositCents(deposit, stayCents);
  const transferOn = call.arrhes === 'transfer' ? transferDue(call, bank ?? null, today) : null;
  const figures: CallFigures = {
    categoryLabel: sale ? label(sale.category) : '[chambre]',
    planLabel: sale ? planLabel(sale.plan) : '[formule]',
    lodged: sale?.lodged ?? false,
    stayCents,
    extrasCents,
    taxCents,
    depositCents: depositValue,
    terms: effectiveTerms,
    today,
    ...(call.arrhes === 'transfer' && bank && transferOn ? { transfer: { details: bank, dueOn: transferOn } } : {}),
    ...(multi
      ? {
          rooms: rooms.map((r) => ({
            categoryLabel: r.sale ? label(r.sale.category) : '[chambre]',
            planLabel: r.sale ? planLabel(r.sale.plan) : '[formule]',
            lodged: r.sale?.lodged ?? false,
            party: r,
            stayCents: r.sale ? stayTotal(r.sale, r.overrideCents) : 0,
          })),
        }
      : {}),
  };
  const checks = checklist(call, sale, bank ?? null, today);
  // The rooms the sale will occupy (the host, when lodged) and which are free all stay.
  const hostRooms = useMemo(() => (sale ? categoryRooms(units, roomTypes, sale.host, propertyCode) : []), [sale, units, roomTypes, propertyCode]);
  const stayFrom = query?.from ?? null;
  const stayTo = query?.to ?? null;
  const givenElsewhere = useMemo(
    () => new Set(call.rooms.filter((_, i) => i !== call.active).flatMap((r) => (r.roomId === null ? [] : [r.roomId]))),
    [call.rooms, call.active],
  );
  const free = useMemo(() => {
    if (!stays || !stayFrom || !stayTo) return null;
    const ids = freeRoomIds(hostRooms, stays, stayFrom, stayTo);
    for (const id of givenElsewhere) ids.delete(id);
    return ids;
  }, [hostRooms, stays, stayFrom, stayTo, givenElsewhere]);

  // The plan's own policy, from the policies read once.
  useEffect(() => {
    const p = call.pick ? ratePlans.find((x) => x.code === call.pick!.plan) : undefined;
    const pol = p ? policies.find((x) => x.id === p.cancellationPolicyId) : undefined;
    setPlanTerms(pol ? policyRules(pol) : null);
  }, [ratePlans, call.pick, policies]);

  const go = (s: CallStep) => {
    if (AFTER_YES.has(s) && !booking) return;
    setStep(s);
  };
  const idx = steps.indexOf(step);
  const next = idx >= 0 && idx < steps.length - 1 ? steps[idx + 1]! : null;

  const restart = () => {
    setCall(emptyCall(propertyCode));
    setStep('accueil');
    setSaved(null);
    setAssigned({ given: [], failed: [] });
    setMail(null);
    setMailError('');
    setCardLink('');
    setCounted('');
    setError('');
    setStartedAt(Date.now());
  };

  // « Nouvel appel » after an enquiry: counted, anonymously (PB9) — once.
  const newCall = async () => {
    if (call.decision === 'info' && !counted) {
      const t = buildTurnaway(call, options, catalogue, call.overrideCents);
      if (t) {
        const r = await services.turnaways.record(t);
        if (!r.ok) {
          setError(`Demande non comptée : ${r.error.message}`);
          return;
        }
      }
    }
    restart();
  };

  const save = async () => {
    const built = buildCallCommand(call, sale, catalogue);
    if (!built.ok) {
      setError(built.reason);
      return;
    }
    setBusy(true);
    setError('');
    const r = await services.reservations.apply(built.command);
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setSaved({ ref: bookingRef(r.value), id: r.value });
    await assignGiven(r.value);
    onSaved();
    void prepareMail(r.value, bookingRef(r.value));
  };

  /** The rooms given at the call, assigned now the logements exist: the journal's
   *  « Chambre attribuée » records when, and by whom. A refusal (taken meanwhile)
   *  leaves the booking saved and the room to the planning. */
  const assignGiven = async (reservationId: string) => {
    if (rooms.every((x) => x.roomId === null)) return;
    const number = (id: number) => units.find((u) => u.id === id)?.roomNumber ?? String(id);
    const created = await services.reservations.roomStays(reservationId);
    if (!created.ok) {
      setAssigned({ given: [], failed: rooms.flatMap((x) => (x.roomId === null ? [] : [number(x.roomId)])) });
      return;
    }
    const typeId = (code: string) => roomTypes.find((t) => t.code === code && t.propertyCode === propertyCode)?.id;
    const given: string[] = [];
    const failed: string[] = [];
    const wanted = roomAssignments(rooms, [...created.value].sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true })), typeId);
    for (const a of wanted) {
      const done = await services.reservations.assignRoomStay(a.stayId, a.roomId);
      (done.ok ? given : failed).push(number(a.roomId));
    }
    setAssigned({ given, failed });
  };

  /** The confirmation, stored with the desk's text (M2): to the guest's e-mail, waiting for the card link. */
  const prepareMail = async (reservationId: string, ref: string) => {
    if (!call.email.trim()) return;
    setMailError('');
    const signer = desk.current ? (desk.current.nickname?.trim() || desk.current.firstName) : null;
    const m = confirmationMail(call, figures, ref, signer);
    const p = await services.confirmationMail.prepare({ reservationId, to: call.email.trim(), ...m });
    if (!p.ok) {
      setMailError(p.error.message);
      return;
    }
    await reloadMail(reservationId);
  };

  const reloadMail = async (reservationId: string) => {
    const l = await services.confirmationMail.latest(reservationId);
    if (l.ok) {
      setMail(l.value);
      if (l.value?.cardLink) setCardLink(l.value.cardLink);
    } else setMailError(l.error.message);
  };

  /** « Envoyer la confirmation » / « Renvoyer »: the link set (when changed), then the send. */
  const sendMail = async () => {
    if (!mail || !saved) return;
    setMailBusy(true);
    setMailError('');
    // A bank-transfer confirmation has no card link to set (PB10).
    if (call.arrhes === 'vad' && cardLink.trim() !== (mail.cardLink ?? '')) {
      const l = await services.confirmationMail.setLink(mail.id, cardLink);
      if (!l.ok) {
        setMailBusy(false);
        setMailError(l.error.message);
        return;
      }
    }
    const r = await services.confirmationMail.send(mail.id);
    setMailBusy(false);
    if (!r.ok) setMailError(r.error.message);
    else if (!r.value.sent && r.value.reason) setMailError(r.value.reason);
    await reloadMail(saved.id);
  };

  // ── Steps ──────────────────────────────────────────────────────────────────

  /** What to say: the key points (default), or the full sentences when « Texte à dire » is on. */
  const say = (text: string, points: readonly string[]) => <Cue text={text} points={points} full={showScript} />;

  const stepper = (value: number, setValue: (n: number) => void, min: number, max: number, aria: string) => (
    <span className="pms-call-stepper">
      <button type="button" className="pms-chip" aria-label={`${aria} : moins`} onClick={() => setValue(Math.max(min, value - 1))}>
        −
      </button>
      <strong aria-label={aria}>{value}</strong>
      <button type="button" className="pms-chip" aria-label={`${aria} : plus`} onClick={() => setValue(Math.min(max, value + 1))}>
        +
      </button>
    </span>
  );

  const quoteBox = sale && (
    <div className="pms-call-quote">
      <strong>
        {label(sale.category)} · {planLabel(sale.plan)}
      </strong>
      {sale.restrictions.length > 0 && (
        <span className="pms-tag warning" title="Restriction de la grille — vendable au téléphone, refusée en ligne">
          ⚠ {restrictionsFr(sale.restrictions, sale.maxStay)}
        </span>
      )}
      {sale.lodged && (
        <span className="pms-tag">
          Logement prévu : {label(sale.host)}
          {sale.upgrade ? ' (surclassement)' : ''}
        </span>
      )}
      <span>{datesReadback(call)} · {partyFr(call)}</span>
      <span>
        {amt(roomStayCents)}
        {call.overrideCents !== null ? ' · prix négocié' : ''} — taxe de séjour en plus : {amt(sale.quote.taxCents)}
      </span>
    </div>
  );

  // Several rooms (operator, 2026-10-27): the same dates, one party and one
  // choice per room. « + Une autre chambre » keeps this one and opens the next.
  const roomTabs = (
    <div className="pms-call-rooms" role="group" aria-label="Chambres de la réservation">
      {multi &&
        rooms.map((r, i) => (
          <span key={i} className="pms-call-room">
            <button
              type="button"
              className={`pms-chip ${i === call.active ? 'is-active' : ''}`}
              aria-pressed={i === call.active}
              onClick={() => setCall(switchRoom(call, sale, i))}
            >
              Chambre {i + 1} · {r.sale ? r.sale.category : 'à choisir'}
              {r.roomId !== null ? ` · n° ${units.find((u) => u.id === r.roomId)?.roomNumber ?? r.roomId}` : ''}
            </button>
            <button type="button" className="pms-link" aria-label={`Retirer la chambre ${i + 1}`} onClick={() => setCall(removeRoom(call, sale, i))}>
              ×
            </button>
          </span>
        ))}
      <button
        type="button"
        className="pms-chip"
        disabled={!sale}
        title={sale ? undefined : 'Choisissez d’abord la chambre en cours'}
        onClick={() => {
          setCall(addRoom(call, sale));
          setStep('dates');
        }}
      >
        + Une autre chambre
      </button>
    </div>
  );

  const body = (() => {
    if (saved) {
      return (
        <section>
          <h3>Réservation enregistrée — {saved.ref}</h3>
          {say(
            call.arrhes === 'transfer'
              ? `« Votre numéro de réservation est le ${saved.ref.split('').join(' ')} : indiquez-le sur votre virement. Vous recevrez nos coordonnées bancaires par e-mail dans quelques minutes. Merci de votre appel, et à bientôt ! »`
              : `« Votre numéro de réservation est le ${saved.ref.split('').join(' ')}. Vous recevrez le lien sécurisé par e-mail dans quelques minutes. Merci de votre appel, et à bientôt ! »`,
            call.arrhes === 'transfer'
              ? ['Le numéro de réservation, lettre par lettre : à indiquer sur le virement', 'Les coordonnées bancaires arrivent par e-mail', 'Remercier']
              : ['Le numéro de réservation, lettre par lettre', 'Le lien sécurisé arrive par e-mail', 'Remercier'],
          )}
          <h4>À faire maintenant</h4>
          <ul>
            <li>
              {call.arrhes === 'transfer'
                ? `Virement : arrhes de ${amt(depositValue)} à recevoir${transferOn ? ` avant le ${fmtDate(transferOn)}` : ''} — la réservation apparaît dans Réservations sous « Virement à recevoir » ; « Virement reçu » la clôt.`
                : `VAD ${depositValue > 0 ? `: arrhes de ${amt(depositValue)}` : 'sans arrhes : la carte garantit, rien n’est prélevé'} — la réservation apparaît dans Réservations sous « VAD … ».`}
            </li>
            {assigned.given.length > 0 && <li>Chambre{assigned.given.length > 1 ? 's' : ''} attribuée{assigned.given.length > 1 ? 's' : ''} : {assigned.given.join(', ')}.</li>}
            {assigned.failed.length > 0 && (
              <li className="pms-call-warn">
                Chambre{assigned.failed.length > 1 ? 's' : ''} {assigned.failed.join(', ')} non attribuée{assigned.failed.length > 1 ? 's' : ''} (prise
                entre-temps ?) : à faire au planning.
              </li>
            )}
            {rooms.some((x) => x.roomId === null) && (
              <li>
                Planning : attribuer {rooms.filter((x) => x.roomId === null).length > 1 ? 'les chambres' : 'une chambre'} (
                {[...new Set(rooms.filter((x) => x.roomId === null && x.sale).map((x) => label(x.sale!.host)))].join(', ')}).
              </li>
            )}
          </ul>
          <h4>Confirmation par e-mail</h4>
          {!call.email.trim() ? (
            <p className="pms-hint">Pas d’e-mail pour ce client : la confirmation est à envoyer à la main (texte ci-dessous).</p>
          ) : (
            <div className="pms-call-mail">
              {mail && <p className={`pms-call-mail-status is-${mail.status}`}>{confirmationStatusFr(mail)}</p>}
              {mailError && <p className="auth-err">{mailError}</p>}
              {!mail && mailError && (
                <button type="button" className="auth-btn" onClick={() => void prepareMail(saved.id, saved.ref)}>
                  Préparer la confirmation
                </button>
              )}
              {mail && mail.status !== 'sent' && mail.status !== 'sending' && (
                <>
                  {call.arrhes === 'vad' && (
                    <label className="pms-field">
                      <span>Lien carte (VAD) — créé sur le portail de la banque</span>
                      <input type="url" inputMode="url" placeholder="https://…" value={cardLink} onChange={(e) => setCardLink(e.target.value)} />
                    </label>
                  )}
                  {call.arrhes === 'transfer' && <p className="pms-hint">Les coordonnées bancaires et l’échéance sont dans le texte : rien à coller.</p>}
                  <button type="button" className="auth-btn" disabled={mailBusy || (call.arrhes === 'vad' && !cardLink.trim())} onClick={() => void sendMail()}>
                    {mailBusy ? 'Envoi…' : mail.status === 'failed' ? 'Renvoyer' : 'Envoyer la confirmation'} à {mail.to}
                  </button>
                </>
              )}
            </div>
          )}
          <textarea className="pms-call-text" readOnly rows={14} value={confirmationText(call, figures, saved.ref)} />
          <p>
            <button type="button" className="auth-btn" onClick={() => void navigator.clipboard?.writeText(confirmationText(call, figures, saved.ref))}>
              Copier
            </button>{' '}
            <button type="button" className="auth-btn" onClick={restart}>
              Nouvel appel
            </button>
          </p>
        </section>
      );
    }
    if (call.decision === 'info') {
      const t = buildTurnaway(call, options, catalogue, call.overrideCents);
      return (
        <section>
          <h3>Renseignement — appel terminé</h3>
          {say(`« Je vous en prie. Les disponibilités évoluent vite : n’hésitez pas à nous rappeler${sale ? ' si cette offre vous intéresse' : ''}. Très bonne journée ! »`, ['Remercier', 'Les disponibilités changent vite : rappeler si l’offre intéresse'])}
          {quoteBox}
          <p className="pms-hint">
            {t
              ? `À « Nouvel appel », la demande est comptée, anonyme (${t.quoted ? 'offre déclinée' : 'sans disponibilité'}) : ${datesReadback(call)}, ${partyFr(call)}. Ni nom ni coordonnées. S’il change d’avis, rien n’est compté : la réservation le remplace.`
              : 'Aucune date demandée : rien à compter.'}
          </p>
          <p>
            <button type="button" className="auth-btn" onClick={() => { set({ decision: 'book' }); setStep('coord'); }}>
              Il change d’avis : réserver
            </button>{' '}
            <button type="button" className="auth-btn" onClick={() => void newCall()}>
              Nouvel appel
            </button>
          </p>
        </section>
      );
    }
    switch (step) {
      case 'accueil':
        return (
          <section>
            {say(greetingFr(desk.current?.firstName), [`« Le Montaigu, Bonjour, ${desk.current?.firstName?.trim() || '[votre prénom]'} »`, 'Écouter la demande'])}
            {!desk.current && (
              <p className="pms-hint">
                Identifiez-vous (« Qui êtes-vous ? » en haut) : votre prénom s’affiche ici, et la réservation est enregistrée à votre nom.
              </p>
            )}
            <fieldset className="pms-call-group">
              <legend>Langue de l’appel</legend>
              {LANGS.map((l) => (
                <button key={l.v} type="button" className={`pms-chip ${call.lang === l.v ? 'is-active' : ''}`} onClick={() => set({ lang: l.v })}>
                  {l.label}
                </button>
              ))}
            </fieldset>
            <fieldset className="pms-call-group">
              <legend>Qui appelle ?</legend>
              <button type="button" className={`pms-call-lane ${call.lane === 'new' ? 'is-active' : ''}`} onClick={() => { set({ lane: 'new' }); setStep('dates'); }}>
                <strong>« Je voudrais savoir si vous avez une chambre… »</strong>
                <span>Nouvel appelant, ou on ne sait pas encore. On répond d’abord ; le nom n’est demandé que s’il réserve.</span>
              </button>
              <button type="button" className={`pms-call-lane ${call.lane === 'known' ? 'is-active' : ''}`} onClick={() => { set({ lane: 'known' }); setStep('client'); }}>
                <strong>« Je suis déjà venu(e) chez vous… »</strong>
                <span>Client qui revient : on cherche sa fiche tout de suite pour voir son historique.</span>
              </button>
            </fieldset>
          </section>
        );
      case 'client':
        return <ClientStep call={call} set={set} services={services} reservations={reservations} full={showScript} onNext={() => setStep('dates')} />;
      case 'dates':
        return (
          <section>
            {roomTabs}
            {say('« Pour quelles dates souhaitez-vous venir ? Ce sera pour combien de nuits ? »', ['Date d’arrivée', 'Nombre de nuits'])}
            <div className="pms-call-row">
              <label>
                Arrivée{' '}
                <input type="date" min={today} value={call.arrival} onChange={(e) => set({ arrival: e.target.value, pick: null })} />
              </label>
              <span>Nuits {stepper(call.nights, (n) => set({ nights: n, pick: null }), 1, 30, 'Nuits')}</span>
              <span>Départ : {departureOf(call) ? fmtDate(departureOf(call)!) : '—'}</span>
            </div>
            <p className="pms-hint">À relire au client : « {datesReadback(call)}. »</p>
            {say(
              multi
                ? `« Dans la chambre ${call.active + 1}, vous serez combien ? Combien d’adultes ? Des enfants ? Quel âge ont-ils ? »`
                : '« Vous serez combien dans la chambre ? Combien d’adultes ? Y aura-t-il des enfants ? Quel âge ont-ils ? »',
              [multi ? `Chambre ${call.active + 1} : nombre d’adultes` : 'Nombre d’adultes', 'Enfants : l’âge de chacun', 'Plusieurs chambres ? « + Une autre chambre »'],
            )}
            <div className="pms-call-row">
              <span>Adultes {stepper(call.adults, (n) => set({ adults: n, pick: null }), 1, 6, 'Adultes')}</span>
              <button type="button" className="pms-chip" onClick={() => set({ childAges: [...call.childAges, null], pick: null })}>
                + Ajouter un enfant
              </button>
            </div>
            {call.childAges.map((a, i) => (
              <div key={i} className="pms-call-row">
                <label>
                  Enfant {i + 1} — âge{' '}
                  <select
                    value={a === null ? '' : String(a)}
                    onChange={(e) => set({ childAges: call.childAges.map((x, j) => (j === i ? (e.target.value === '' ? null : Number(e.target.value)) : x)), pick: null })}
                  >
                    <option value="">?</option>
                    {Array.from({ length: 18 }, (_, n) => (
                      <option key={n} value={n}>
                        {n === 0 ? 'moins d’un an' : `${n} an${n > 1 ? 's' : ''}`}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="button" className="pms-link" onClick={() => set({ childAges: call.childAges.filter((_, j) => j !== i), pick: null })}>
                  Retirer
                </button>
              </div>
            ))}
            {agesMissing(call) && <p className="pms-hint">Demandez l’âge de chaque enfant : un bébé de moins de 3 ans ne compte ni pour la chambre ni pour les repas. En attendant, l’enfant est compté comme un adulte pour les repas.</p>}
            <OptionsTable
              options={options}
              error={optionsError}
              plans={ratePlans}
              label={label}
              held={(host) => heldElsewhere(call, host)}
              picked={call.pick}
              onPick={(category, plan) => { set({ pick: { category, plan }, bed: null, roomId: null, overrideCents: null, deposit: null, relaxed: null }); setStep('offre'); }}
              ready={query !== null}
            />
            {sale && (
              <div className="pms-call-row">
                <label>
                  Prix négocié du séjour, hors taxe de séjour{' '}
                  <AmountInput
                    cents={call.overrideCents}
                    placeholder={amt(sale.quote.totalCents)}
                    onChange={(c) => set({ overrideCents: c })}
                    label="Prix négocié du séjour"
                  />
                </label>
                <span className="pms-sub">
                  {amt(roomStayCents)} soit {amt(Math.round(roomStayCents / sale.quote.nights.length))} / nuit · avec la taxe de séjour : {amt(roomStayCents + sale.quote.taxCents)}
                </span>
              </div>
            )}
          </section>
        );
      case 'offre':
        return (
          <section>
            {roomTabs}
            {sale ? (
              <>
                {say(`« Je peux vous proposer ${label(sale.category).toLowerCase()} en ${planLabel(sale.plan).toLowerCase()}, ${datesReadback(call)}, pour ${amt(roomStayCents)}, taxe de séjour en plus. »`, [`${label(sale.category)}, ${planLabel(sale.plan).toLowerCase()}`, `Le prix : ${amt(roomStayCents)}`, 'Taxe de séjour en plus'])}
                {quoteBox}
              </>
            ) : (
              <p>Aucun prix retenu : choisissez une chambre et une formule à l’étape « Dates et voyageurs ».</p>
            )}
            {say('« Quelle configuration de lits souhaitez-vous ? »', ['Configuration de lits', 'Puis choisir la chambre'])}
            {sale ? (
              <RoomChoices
                call={call}
                set={set}
                hostLabel={label(sale.host)}
                rooms={hostRooms}
                bedConfigs={bedConfigs}
                people={people}
                free={free}
              />
            ) : (
              <p className="pms-hint">Les lits et les chambres dépendent de la catégorie retenue.</p>
            )}
          </section>
        );
      case 'decision':
        return (
          <section>
            {say(
              sale
                ? multi
                  ? `« Je vous propose donc ${rooms.length} chambres, ${datesReadback(call)}, pour ${amt(stayCents)} en tout, taxe de séjour en plus. Souhaitez-vous que je vous les réserve ? »`
                  : `« Je vous propose donc ${label(sale.category).toLowerCase()} en ${planLabel(sale.plan).toLowerCase()}, ${datesReadback(call)}, pour ${amt(stayCents)}, taxe de séjour en plus. Souhaitez-vous que je vous la réserve ? »`
                : '« Souhaitez-vous que je vous réserve une chambre ? »',
              ['Rappeler l’offre et son prix', 'Demander s’il souhaite réserver'],
            )}
            {quoteBox ?? <p>Aucune offre choisie pour l’instant.</p>}
            {multi && <p className="pms-sub">{rooms.length} chambres · {amt(stayCents)} en tout, taxe de séjour {amt(taxCents)} en plus.</p>}
            <p>
              <button type="button" className="auth-btn" disabled={!sale || rooms.some((x) => x.sale === null)} onClick={() => { set({ decision: 'book' }); setStep('coord'); }}>
                Oui, il réserve
              </button>{' '}
              <button type="button" className="auth-btn" onClick={() => set({ decision: 'info' })}>
                Non, simple renseignement
              </button>
            </p>
            <p className="pms-hint">« Non » : fin d’appel. Aucun nom demandé ; la demande est comptée, anonyme.</p>
          </section>
        );
      case 'coord':
        return <CoordStep call={call} set={set} services={services} full={showScript} />;
      case 'details':
        return (
          <section>
            {say('« À quelle heure pensez-vous arriver ? »', ['Heure d’arrivée prévue'])}
            <div className="pms-call-row">
              <label>
                Entre <input type="time" value={call.arrivalFrom} onChange={(e) => set({ arrivalFrom: e.target.value })} />
              </label>
              <label>
                et <input type="time" value={call.arrivalTo} onChange={(e) => set({ arrivalTo: e.target.value })} />
              </label>
            </div>
            {say(`« Venez-vous avec un animal de compagnie ?${call.childAges.some((a) => a !== null && a < 3) ? ' Je vous prévois un lit bébé ?' : ''} »`, ["Un animal de compagnie ?", ...(call.childAges.some((a) => a !== null && a < 3) ? ['Un lit bébé ?'] : [])])}
            {extraProducts(products).map((p) => {
              const qty = call.extras[p.id] ?? 0;
              return (
                <div key={p.id} className="pms-call-row">
                  <span>
                    {p.labelFr} — {amt(p.priceCents)}
                    {p.perPerson ? ' / pers.' : ''}
                  </span>
                  {stepper(qty, (n) => set({ extras: { ...call.extras, [p.id]: n } }), 0, 20, p.labelFr)}
                </div>
              );
            })}
            {say('« Venez-vous pour une occasion particulière ? Y a-t-il des allergies ou un régime à signaler ? »', ['Une occasion particulière ?', 'Allergies ou régime ?'])}
            <div className="pms-call-row">
              {OCCASIONS.map((o) => (
                <button key={o} type="button" className={`pms-chip ${call.occasion === o ? 'is-active' : ''}`} onClick={() => set({ occasion: call.occasion === o ? '' : o })}>
                  {o}
                </button>
              ))}
            </div>
            {canNeeds && (
              <label className="pms-call-col">
                Allergies, régime (besoins)
                <textarea rows={2} value={call.needs} onChange={(e) => set({ needs: e.target.value })} />
                <span className="pms-hint">Données de santé : rangées dans « Besoins », visibles seulement par qui en a le droit.</span>
              </label>
            )}
            <label className="pms-call-col">
              Note pour la réception
              <textarea rows={2} value={call.note} onChange={(e) => set({ note: e.target.value })} />
            </label>
          </section>
        );
      case 'arrhes':
        return (
          <ArrhesStep
            call={call}
            set={set}
            stayCents={stayCents}
            depositValue={depositValue}
            deposit={deposit}
            baseTerms={baseTerms}
            planPolicy={policies.find((x) => x.id === (call.pick ? ratePlans.find((r) => r.code === call.pick!.plan)?.cancellationPolicyId : undefined)) ?? null}
            policies={deskPolicies(policies)}
            bank={bank ?? null}
            bankRead={bank !== undefined}
            dueOn={transferOn}
            today={today}
            full={showScript}
          />
        );
      case 'recap': {
        const missing = checks.filter((c) => !c.ok && c.step !== 'recap');
        return (
          <section>
            {missing.length > 0 && (
              <div className="pms-call-missing">
                <strong>Il manque encore :</strong>
                {missing.map((m) => (
                  <button key={m.label} type="button" className="pms-link" onClick={() => go(m.step)}>
                    {m.label}
                  </button>
                ))}
              </div>
            )}
            <p className="pms-hint">À lire au client, en {call.lang === 'fr' ? 'français' : 'anglais'} :</p>
            <pre className="pms-call-text">{recapText(call, figures)}</pre>
            <label className="pms-call-row">
              <input type="checkbox" checked={call.confirmed} onChange={(e) => set({ confirmed: e.target.checked })} /> Le client a confirmé le récapitulatif
            </label>
            <button type="button" className="auth-btn" disabled={busy || checks.some((c) => !c.ok)} onClick={() => void save()}>
              {busy ? 'Enregistrement…' : 'Enregistrer la réservation'}
            </button>
          </section>
        );
      }
    }
  })();

  return (
    <div className="pms-call" role="region" aria-label="Réservation manuelle">
      <div className="pms-head">
        <h2 className="section-title">Réservation manuelle</h2>
        <label className="pms-chip">
          <input
            type="checkbox"
            checked={showScript}
            onChange={(e) => {
              setShowScript(e.target.checked);
              writeShowScript(e.target.checked);
            }}
          />{' '}
          Script
        </label>
        <span className="pms-hint" aria-label="Durée de l’appel">{mmss(now - startedAt)}</span>
        <button type="button" className="auth-btn" onClick={restart}>
          Recommencer
        </button>
        <button type="button" className="auth-btn" onClick={onClose}>
          Fermer
        </button>
      </div>
      {error && <p className="auth-err">Erreur : {error}</p>}
      <nav className="pms-call-steps" aria-label="Étapes de l’appel">
        {steps.map((s, i) => {
          const locked = AFTER_YES.has(s) && !booking;
          return (
            <button
              key={s}
              type="button"
              className={`pms-chip ${s === step && !saved && call.decision !== 'info' ? 'is-active' : ''}`}
              aria-current={s === step ? 'step' : undefined}
              disabled={locked}
              title={locked ? 'S’ouvre quand le client dit oui' : undefined}
              onClick={() => { if (call.decision === 'info' && s !== 'decision') return; go(s); }}
            >
              {i + 1}. {STEP_LABEL[s]}
            </button>
          );
        })}
      </nav>
      <div className="pms-call-body">
        <div className="pms-call-main">
          {body}
          {!saved && call.decision !== 'info' && (
            <p className="pms-call-nav">
              {idx > 0 && (
                <button type="button" className="auth-btn" onClick={() => setStep(steps[idx - 1]!)}>
                  ← Retour
                </button>
              )}{' '}
              {next && !(step === 'decision' && !booking) && (
                <button type="button" className="auth-btn" onClick={() => go(next)}>
                  Suivant : {STEP_LABEL[next]} →
                </button>
              )}
            </p>
          )}
        </div>
        <aside className="pms-call-card" aria-label="Fiche de la réservation">
          <strong>{saved ? `Réservation ${saved.ref}` : call.decision === 'info' ? 'Renseignement' : 'Nouvelle réservation'}</strong>
          <dl>
            <dt>Client</dt>
            <dd>{`${call.firstName} ${call.lastName.toUpperCase()}`.trim() || '—'}</dd>
            <dt>Séjour</dt>
            <dd>{call.arrival ? datesReadback(call) : '—'}</dd>
            <dt>Voyageurs</dt>
            <dd>{partyFr(party)}</dd>
          </dl>
          <ul className="pms-call-card-rooms" aria-label={multi ? 'Chambres' : 'Chambre'}>
            {rooms.map((r, i) => {
              const unit = r.roomId === null ? null : (units.find((u) => u.id === r.roomId) ?? null);
              return (
                <CardRoom
                  key={i}
                  room={r}
                  unit={unit}
                  unitType={unit === null ? null : (roomTypes.find((t) => t.id === unit.roomTypeId) ?? null)}
                  bed={r.bed === null ? null : (bedConfigs.find((b) => b.code === r.bed) ?? null)}
                  label={label}
                />
              );
            })}
          </ul>
          {sale && (
            <dl>
              <dt>Hébergement</dt>
              <dd>{amt(stayCents)}</dd>
              <dt>Extras</dt>
              <dd>{amt(extrasCents)}</dd>
              <dt>Taxe de séjour</dt>
              <dd>{amt(taxCents)}</dd>
              <dt>Total</dt>
              <dd>
                <strong>{amt(stayCents + extrasCents + taxCents)}</strong>
              </dd>
              <dt>Arrhes ({call.arrhes === 'transfer' ? 'virement' : 'VAD'})</dt>
              <dd>{amt(depositValue)}</dd>
            </dl>
          )}
          {terms && sale && <p className="pms-sub">Conditions du plan : {noSign(terms.fr.prepayment)}</p>}
          <strong>Pour réserver</strong>
          <ul className="pms-call-checks">
            {checks.map((c) => (
              <li key={c.label} className={c.ok ? 'is-ok' : c.later && !booking ? 'is-later' : 'is-miss'}>
                {c.ok ? '✓' : c.later && !booking ? '·' : '!'} {c.label}
                {!c.ok && c.later && !booking ? ' — après le « oui »' : ''}
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}

// ── The options table (PB3) ──────────────────────────────────────────────────

function OptionsTable({
  options,
  error,
  plans,
  label,
  held,
  picked,
  onPick,
  ready,
}: {
  options: DeskSale[];
  error: string;
  plans: RatePlan[];
  label: (code: string) => string;
  /** Rooms of a host category the booking's other rooms already take. */
  held: (host: string) => number;
  picked: CallDraft['pick'];
  onPick: (category: string, plan: string) => void;
  ready: boolean;
}) {
  if (!ready) return <p className="pms-hint">Indiquez l’arrivée pour voir ce qu’on peut proposer.</p>;
  if (error) return <p className="auth-err">Offre indisponible : {error}</p>;
  // A category too small for the party is not an option: not shown (operator, 2026-10-27).
  const categories = [...new Set(options.map((o) => o.category))]
    .filter((cat) => options.some((o) => o.category === cat && (o.sellable || o.reason !== 'too_many_guests')))
    .sort(byCategoryOrder);
  const planCodes = [...new Set(options.map((o) => o.plan))];
  const planName = (code: string) => plans.find((p) => p.code === code)?.labelFr ?? code;
  const nothing = options.every((o) => !o.sellable);
  return (
    <div className="pms-call-options">
      <h4>Ce qu’on peut proposer</h4>
      {nothing && <p className="pms-hint">Rien à vendre pour ce groupe à ces dates.</p>}
      <table className="pms-table">
        <thead>
          <tr>
            <th scope="col">Chambre vendue</th>
            {planCodes.map((p) => (
              <th key={p} scope="col">
                {planName(p)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <tr key={cat}>
              <th scope="row">
                {label(cat)} <LeftChip options={options.filter((o) => o.category === cat)} held={held} />
              </th>
              {planCodes.map((p) => {
                const o = options.find((x) => x.category === cat && x.plan === p);
                if (!o) return <td key={p}>—</td>;
                if (!o.sellable) {
                  return (
                    <td key={p} className="pms-sub">
                      {REASON_FR[o.reason] ?? o.reason}
                      {o.minStay ? ` (${o.minStay} nuits)` : ''}
                    </td>
                  );
                }
                const n = o.quote.nights.length;
                return (
                  <td key={p}>
                    <button
                      type="button"
                      className={`pms-chip ${picked?.category === cat && picked.plan === p ? 'is-active' : ''}`}
                      onClick={() => onPick(cat, p)}
                      aria-label={`${label(cat)} ${planName(p)} ${amt(o.quote.totalCents)}`}
                    >
                      {amt(o.quote.totalCents)}
                    </button>
                    <span className="pms-sub">
                      {' '}
                      {amt(Math.round(o.quote.totalCents / n))}/nuit
                      {o.lodged ? ` · logée en ${label(o.host)}` : ''}
                    </span>
                    {/* CC2 (Q3): the site would refuse this stay; the desk is warned and may sell. */}
                    {o.restrictions.length > 0 && (
                      <span className="pms-tag warning" title="Restriction de la grille — vendable au téléphone, refusée en ligne">
                        {' '}
                        {restrictionsFr(o.restrictions, o.maxStay)}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="pms-hint">Touchez un prix pour le retenir : il passe sur la fiche et à l’étape « Offre ». La pastille : chambres libres sur tout le séjour.</p>
    </div>
  );
}

/** How many are left in the category's host over the stay, as a coloured chip:
 *  red for the last one, amber for 2–3, green beyond (operator, 2026-10-27). The
 *  booking's other rooms are taken off first. */
function LeftChip({ options, held }: { options: readonly DeskSale[]; held: (host: string) => number }) {
  const o = options.find((x): x is SoldSale => x.sellable);
  if (!o) {
    const full = options.some((x) => !x.sellable && x.reason === 'full');
    return full ? (
      <span className="pms-call-left is-none" aria-label="0 libre">
        0
      </span>
    ) : null;
  }
  const n = Math.max(0, o.available - held(o.host));
  const tone = n === 0 ? 'is-none' : n === 1 ? 'is-last' : n <= 3 ? 'is-few' : 'is-many';
  return (
    <span className={`pms-call-left ${tone}`} aria-label={`${n} libre${n > 1 ? 's' : ''}${o.lodged ? ` en ${o.host}` : ''}`} title={o.lodged ? `Libres en ${o.host} (logée plus grand)` : 'Libres sur tout le séjour'}>
      {n}
    </span>
  );
}

/** One room on the booking card, on its own row: « TSTD PdJ », who sleeps
 *  in it, the room, its price (operator, 2026-10-28). The room given is drawn
 *  as « Configuration » draws it — beds as made up, view, bath, WC, category,
 *  number; before one is given, the beds asked for. */
function CardRoom({
  room,
  unit,
  unitType,
  bed,
  label,
}: {
  room: CallRoom;
  unit: Room | null;
  unitType: RoomType | null;
  bed: BedConfig | null;
  label: (code: string) => string;
}) {
  const s = room.sale;
  const sold = s ?? room.pick;
  // Only the configuration chosen for this booking, never the room's alternatives (operator, 2026-10-28).
  const beds = bed ? [{ code: bed.code, layout: bed.layout }] : [];
  const occupancy = unitType?.maxOccupancy ?? unit?.maxOccupancy ?? null;
  return (
    <li className="pms-call-card-room">
      <span className="pms-call-card-sold" title={s ? `${label(s.category)}${s.lodged ? ` — logée en ${label(s.host)}` : ''}` : undefined}>
        {sold ? `${sold.category} ${planShort(sold.plan)}`.trim() : 'À choisir'}
        {s?.lodged ? ` (en ${s.host})` : ''}
      </span>
      <span>{partyFr(room)}</span>
      <span className="pms-call-card-price">{s ? amt(stayTotal(s, room.overrideCents)) : '—'}</span>
      {unit !== null ? (
        <span className="pms-call-card-unit" aria-label={`Chambre ${unit.roomNumber}${bed ? `, ${bed.labelFr}` : ''}`}>
          {bed && <BedIcons layouts={beds} doubleBedType={unit.doubleBedType} current={bed.code} />}
          <RoomGlyphs room={unit} />
          {unitType && <span className={`pms-call-card-cat ${occupancy === null ? '' : `pms-occ is-${occupancy}`}`}>{unitType.code}</span>}
          <strong className="pms-call-card-no">{unit.roomNumber}</strong>
        </span>
      ) : bed !== null ? (
        <span className="pms-call-card-unit" aria-label={`Chambre à attribuer, ${bed.labelFr}`}>
          <BedIcons layouts={beds} current={bed.code} />
        </span>
      ) : (
        <span className="pms-sub">lits à choisir</span>
      )}
    </li>
  );
}

// ── Fiche client (the returning lane) ────────────────────────────────────────

function ClientStep({
  call,
  set,
  services,
  reservations,
  full,
  onNext,
}: {
  call: CallDraft;
  set: (p: Partial<CallDraft>) => void;
  services: PmsServices;
  reservations: readonly Reservation[];
  full: boolean;
  onNext: () => void;
}) {
  const pick = (h: GuestSummary) =>
    set({ guestId: h.id, lastName: h.lastName, firstName: h.firstName, phone: h.phone ?? h.mobile ?? '', email: h.email ?? '', lang: h.lang });
  const history = call.guestId
    ? reservations.filter((r) => r.guestId === call.guestId).sort((a, b) => b.checkinDate.localeCompare(a.checkinDate))
    : [];
  return (
    <section>
      <Cue text="« Avec plaisir ! À quel nom aviez-vous réservé ? »" points={['Sous quel nom avait-il réservé ?']} full={full} />
      {/* The booking form's own picker: it searches as you type (no Enter key on a
          phone) and says why when the search is refused. */}
      <GuestPicker guests={services.guests} onPick={pick} />
      {call.guestId && <p className="pms-sub">Fiche : {call.lastName.toUpperCase()} {call.firstName}</p>}
      {call.guestId && (
        <>
          <h4>Historique</h4>
          {history.length === 0 && <p className="pms-hint">Aucun séjour enregistré dans le PMS pour cette fiche.</p>}
          <ul className="pms-call-list">
            {history.slice(0, 5).map((r) => (
              <li key={r.id}>
                {fmtDate(r.checkinDate)} → {fmtDate(r.checkoutDate)} · {r.adults} adulte{r.adults > 1 ? 's' : ''}
                {r.children ? `, ${r.children} enfant${r.children > 1 ? 's' : ''}` : ''} · {amt(r.totalCents)}{' '}
                <button
                  type="button"
                  className="pms-link"
                  onClick={() => {
                    // « Comme cette fois-là »: the party again — dates asked, children's ages re-checked.
                    set({ adults: r.adults, childAges: Array.from({ length: r.children }, () => null), pick: null });
                    onNext();
                  }}
                >
                  Comme cette fois-là
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="pms-link" onClick={() => set({ guestId: null, lastName: '', firstName: '', phone: '', email: '' })}>
            Ce n’est pas cette personne
          </button>
        </>
      )}
      <p>
        <button type="button" className="pms-link" onClick={() => { set({ lane: 'new', guestId: null }); onNext(); }}>
          Pas de fiche : continuer comme nouvel appelant
        </button>
      </p>
    </section>
  );
}

// ── Coordonnées (after « oui ») ──────────────────────────────────────────────

function CoordStep({ call, set, services, full }: { call: CallDraft; set: (p: Partial<CallDraft>) => void; services: PmsServices; full: boolean }) {
  const [dupes, setDupes] = useState<GuestSummary[]>([]);
  const [forOther, setForOther] = useState(call.occupantName !== '');
  const lastChecked = useRef('');
  // The same e-mail on another fiche (operator, 2026-10-27): one guest, one fiche.
  const [sameEmail, setSameEmail] = useState<GuestSummary[]>([]);
  const lastEmail = useRef('');
  const checkEmail = async () => {
    const email = call.email.trim().toLowerCase();
    if (!emailOk(email) || email === lastEmail.current) return;
    lastEmail.current = email;
    const r = await services.guests.search({ query: email, limit: 5 });
    setSameEmail(r.ok ? r.value.hits.filter((h) => (h.email ?? '').trim().toLowerCase() === email && h.id !== call.guestId) : []);
  };
  // A typed name matching a fiche asks « êtes-vous déjà venu ? » rather than create a duplicate (§2).
  const checkDupes = async () => {
    const name = call.lastName.trim();
    if (call.guestId || name.length < 2 || name === lastChecked.current) return;
    lastChecked.current = name;
    const r = await services.guests.search({ query: name, limit: 5 });
    setDupes(r.ok ? r.value.hits.filter((h) => h.lastName.toUpperCase() === name.toUpperCase()) : []);
  };
  return (
    <section>
      {call.guestId ? (
        <Cue
          text={`« Vos coordonnées sont-elles toujours les mêmes : le ${call.phone}, et ${speakEmail(call.email)} ? »`}
          points={['Vérifier le téléphone', 'Vérifier l’e-mail']}
          full={full}
        />
      ) : (
        <Cue
          text="« Très bien, je vous la réserve. À quel nom, s’il vous plaît ? Pouvez-vous me l’épeler ? Un numéro où vous joindre, et une adresse e-mail pour la confirmation ? »"
          points={['Nom, à faire épeler', 'Prénom', 'Téléphone', 'E-mail pour la confirmation']}
          full={full}
        />
      )}
      <div className="pms-call-row">
        <label>
          Nom <input value={call.lastName} onChange={(e) => set({ lastName: e.target.value })} onBlur={() => void checkDupes()} />
        </label>
        <label>
          Prénom <input value={call.firstName} onChange={(e) => set({ firstName: e.target.value })} />
        </label>
      </div>
      {call.lastName.trim() && (
        <p className="pms-sub" aria-label="Épellation">
          {spellOut(call.lastName)
            .map((s) => (s.word ? `${s.ch} comme ${s.word}` : s.ch))
            .join(' · ')}
        </p>
      )}
      {dupes.length > 0 && (
        <div className="pms-call-missing">
          Une fiche existe déjà à ce nom — « Êtes-vous déjà venu(e) chez nous ? » Si oui, choisissez-la :
          {dupes.map((h) => (
            <button
              key={h.id}
              type="button"
              className="pms-chip"
              onClick={() => { set({ guestId: h.id, firstName: h.firstName, phone: h.phone ?? h.mobile ?? call.phone, email: h.email ?? call.email }); setDupes([]); }}
            >
              {h.lastName} {h.firstName} {h.city ? `· ${h.city}` : ''}
            </button>
          ))}
        </div>
      )}
      <div className="pms-call-row">
        <label>
          Téléphone <input type="tel" value={call.phone} onChange={(e) => set({ phone: e.target.value })} />
        </label>
        <label>
          E-mail <input type="email" value={call.email} onChange={(e) => set({ email: e.target.value })} onBlur={() => void checkEmail()} />
        </label>
      </div>
      {sameEmail.length > 0 && (
        <div className="pms-call-missing" role="alert">
          Cet e-mail est déjà sur {sameEmail.length > 1 ? 'des fiches' : 'une fiche'} — « Êtes-vous déjà venu(e) chez nous ? » Si c’est la même personne,
          choisissez la fiche :
          {sameEmail.map((h) => (
            <button
              key={h.id}
              type="button"
              className="pms-chip"
              onClick={() => {
                set({ guestId: h.id, lastName: h.lastName, firstName: h.firstName, phone: h.phone ?? h.mobile ?? call.phone, email: h.email ?? call.email });
                setSameEmail([]);
              }}
            >
              {h.lastName} {h.firstName} {h.city ? `· ${h.city}` : ''}
            </button>
          ))}
          <button type="button" className="pms-link" onClick={() => setSameEmail([])}>
            Personne différente
          </button>
        </div>
      )}
      {call.email.trim() && <p className="pms-sub">À relire : « {speakEmail(call.email)} »</p>}
      <label className="pms-call-row">
        <input type="checkbox" checked={forOther} onChange={(e) => { setForOther(e.target.checked); if (!e.target.checked) set({ occupantName: '' }); }} /> La chambre est pour une autre personne
      </label>
      {forOther && (
        <label className="pms-call-row">
          Nom de l’occupant <input value={call.occupantName} onChange={(e) => set({ occupantName: e.target.value })} />
        </label>
      )}
    </section>
  );
}

// ── Arrhes et conditions ─────────────────────────────────────────────────────

/** A policy on one line, for the picker. */
const policyLine = (p: StoredCancellationPolicy) => `${p.labelFr} — arrhes ${p.prepaymentPct} %, non-présentation ${p.noShowPct} %`;

function ArrhesStep({
  call,
  set,
  stayCents,
  depositValue,
  deposit,
  baseTerms,
  planPolicy,
  policies,
  bank,
  bankRead,
  dueOn,
  today,
  full,
}: {
  call: CallDraft;
  set: (p: Partial<CallDraft>) => void;
  stayCents: number;
  depositValue: number;
  deposit: NonNullable<CallDraft['deposit']>;
  /** The rules the booking starts from: the picked policy's, else the plan's. */
  baseTerms: TermsRules | null;
  planPolicy: StoredCancellationPolicy | null;
  /** The policies the desk may pick (the operator's, not the OTAs' mirrors). */
  policies: readonly StoredCancellationPolicy[];
  bank: BankTransferDetails | null;
  /** False while the instructions are being read. */
  bankRead: boolean;
  dueOn: string | null;
  today: string;
  full: boolean;
}) {
  const terms = termsFor(call, baseTerms);
  const rendered = terms && call.arrival ? renderTerms(terms, 'fr', { arrival: call.arrival, totalCents: stayCents, bookedOn: today }) : null;
  const relaxed = call.relaxed;
  const transfer = call.arrhes === 'transfer';
  // The relaxation edits the familiar shape: one threshold, before and after, the no-show.
  const base = relaxed ?? baseTerms ?? { prepaymentPct: 0, prepaymentAt: 'booking' as const, tiers: [{ moreThanDaysBefore: 5, retainPct: 30 }, { moreThanDaysBefore: null, retainPct: 50 }], noShowPct: 100 };
  const days = base.tiers.length > 1 ? (base.tiers[0]!.moreThanDaysBefore ?? 5) : 5;
  const early = base.tiers.length > 1 ? base.tiers[0]!.retainPct : base.tiers[0]!.retainPct;
  const late = base.tiers[base.tiers.length - 1]!.retainPct;
  const relax = (patch: { days?: number; early?: number; late?: number; noShow?: number }) =>
    set({
      relaxed: {
        prepaymentPct: base.prepaymentPct,
        prepaymentAt: base.prepaymentAt,
        tiers: [
          { moreThanDaysBefore: patch.days ?? days, retainPct: patch.early ?? early },
          { moreThanDaysBefore: null, retainPct: patch.late ?? late },
        ],
        noShowPct: patch.noShow ?? base.noShowPct,
      },
    });
  const policyPct = baseTerms?.prepaymentAt === 'booking' ? baseTerms.prepaymentPct : null;
  return (
    <section>
      <fieldset className="pms-call-group">
        <legend>Paiement des arrhes</legend>
        {(['vad', 'transfer'] as const).map((m) => (
          <button
            key={m}
            type="button"
            className={`pms-chip ${call.arrhes === m ? 'is-active' : ''}`}
            aria-pressed={call.arrhes === m}
            disabled={m === 'transfer' && bank === null}
            // A transfer of nothing is meaningless: the policy's own percentage is offered, visibly, and stays the desk's to change.
            onClick={() => set({ arrhes: m, ...(m === 'transfer' && depositValue === 0 && policyPct ? { deposit: { kind: 'pct' as const, pct: policyPct } } : {}) })}
          >
            {ARRHES_METHOD_LABELS_FR[m]}
          </button>
        ))}
        {bankRead && bank === null && (
          <p className="pms-call-warn">
            Aucune coordonnée bancaire pour cet établissement : un virement ne peut pas être proposé. À saisir dans Configuration › Conditions.
          </p>
        )}
      </fieldset>
      {transfer ? (
        <Cue
          text={`« Pour confirmer la réservation, je vous envoie par e-mail nos coordonnées bancaires : des arrhes de ${amt(depositValue)} euros sont à virer avant le ${dueOn ? fmtDate(dueOn) : '…'}. Indiquez le numéro de réservation sur le virement. Le solde se règle sur place. »`}
          points={['Coordonnées bancaires par e-mail', `Arrhes de ${amt(depositValue)} à virer avant le ${dueOn ? fmtDate(dueOn) : '…'}`, 'Numéro de réservation sur le virement', 'Solde sur place']}
          full={full}
        />
      ) : depositValue > 0 ? (
        <Cue
          text={`« Pour confirmer la réservation, nous vous envoyons par e-mail un lien sécurisé où saisir votre carte ; nous y prélèverons des arrhes de ${amt(depositValue)} euros. Le solde se règle sur place. »`}
          points={['Lien sécurisé par e-mail pour saisir la carte', `Arrhes de ${amt(depositValue)} prélevées`, 'Solde sur place']}
          full={full}
        />
      ) : (
        <Cue
          text="« Pour garantir la réservation, nous vous envoyons par e-mail un lien sécurisé où saisir votre carte. Rien n’est prélevé aujourd’hui : elle ne sert qu’en cas d’annulation tardive ou de non-présentation. »"
          points={['Lien sécurisé par e-mail pour saisir la carte', 'Rien n’est prélevé', 'La carte garantit : annulation tardive, non-présentation']}
          full={full}
        />
      )}
      <fieldset className="pms-call-group">
        <legend>Arrhes demandées{transfer ? ' par virement' : ' par VAD'}</legend>
        {[0, 30, 50, 100].map((p) => (
          <button
            key={p}
            type="button"
            className={`pms-chip ${deposit.kind === 'pct' && deposit.pct === p ? 'is-active' : ''}`}
            disabled={transfer && p === 0}
            onClick={() => set({ deposit: { kind: 'pct', pct: p } })}
          >
            {p} %
          </button>
        ))}
        <label>
          Montant{' '}
          <AmountInput
            cents={deposit.kind === 'amount' ? deposit.cents : null}
            placeholder={amt(depositValue)}
            onChange={(c) => set({ deposit: c === null ? null : { kind: 'amount', cents: c } })}
            label="Montant des arrhes"
          />
        </label>
        {policyPct !== null && policyPct > 0 && !(deposit.kind === 'pct' && deposit.pct === policyPct) && (
          <button type="button" className="pms-link" onClick={() => set({ deposit: { kind: 'pct', pct: policyPct } })}>
            Selon la politique : {policyPct} %
          </button>
        )}
        <p className="pms-hint">
          Arrhes : {amt(depositValue)}.{' '}
          {transfer
            ? 'Elles sont à recevoir par virement : la réservation est confirmée, « Virement à recevoir » jusqu’à leur arrivée.'
            : 'Dans tous les cas la carte est conservée, en sécurité hors du PMS, jusqu’à ce qu’une pénalité s’applique. À l’enregistrement : Confirmée · VAD à traiter.'}
        </p>
      </fieldset>
      {transfer && (
        <fieldset className="pms-call-group">
          <legend>Échéance du virement</legend>
          <label>
            À recevoir avant le{' '}
            <input
              type="date"
              min={today}
              max={call.arrival || undefined}
              value={dueOn ?? ''}
              onChange={(e) => set({ transferDueOn: e.target.value as CallDraft['transferDueOn'] })}
            />
          </label>
          {call.transferDueOn !== '' && (
            <button type="button" className="pms-link" onClick={() => set({ transferDueOn: '' })}>
              Revenir au délai de l’établissement
            </button>
          )}
          <p className="pms-hint">Par défaut {bank?.dueDays ?? 7} jours après la réservation, jamais après l’arrivée. Passée l’échéance, la réception relance ou annule : rien ne s’annule seul.</p>
          {bank && (
            <>
              <strong>Ce que le client recevra par e-mail</strong>
              <pre className="pms-call-text">
                {transferInstructionLines(
                  { details: bank, amountCents: depositValue, dueOn: dueOn ?? today, reference: '[numéro de réservation]' },
                  'fr',
                ).join('\n')}
              </pre>
              <p className="pms-sub">Compte : {bank.accountHolder} · {formatIban(bank.iban)}</p>
            </>
          )}
        </fieldset>
      )}
      <h4>
        Conditions de réservation et d’annulation
        {relaxed ? ' — assouplies pour ce client' : ''}
      </h4>
      <label className="pms-field">
        <span>Politique</span>
        <select
          value={call.policyCode ?? ''}
          onChange={(e) => set({ policyCode: e.target.value === '' ? null : e.target.value, relaxed: null })}
          aria-label="Politique de réservation et d’annulation"
        >
          <option value="">{planPolicy ? `Celle du plan — ${planPolicy.labelFr}` : 'Celle du plan (aucune définie)'}</option>
          {policies
            .filter((p) => p.id !== planPolicy?.id)
            .map((p) => (
              <option key={p.code} value={p.code}>
                {policyLine(p)}
              </option>
            ))}
        </select>
        <span className="pms-sub">À choisir selon la saison, la période ou le remplissage. Les politiques se créent dans Configuration › Conditions.</span>
      </label>
      {rendered ? (
        <>
          <Cue
            text={`« ${rendered.cancellation.join(' ')} ${rendered.noShow} »`.replace(/\s?€/g, ' euros')}
            points={[...rendered.cancellation, rendered.noShow].map(noSign)}
            full={full}
          />
          <p className="pms-sub">{rendered.basis}</p>
        </>
      ) : (
        <p className="pms-hint">Ce plan ne suit aucune condition d’annulation : choisissez une politique, ou réglez-les ci-dessous.</p>
      )}
      <details>
        <summary>Assouplir les conditions pour ce client</summary>
        <div className="pms-call-row">
          <label>
            Seuil (jours avant l’arrivée) <input type="number" min={1} max={365} value={days} onChange={(e) => relax({ days: Number(e.target.value) })} />
          </label>
          <label>
            Retenu avant le seuil (%) <input type="number" min={0} max={100} value={early} onChange={(e) => relax({ early: Number(e.target.value) })} />
          </label>
          <label>
            Retenu après (%) <input type="number" min={0} max={100} value={late} onChange={(e) => relax({ late: Number(e.target.value) })} />
          </label>
          <label>
            Non-présentation (%) <input type="number" min={0} max={100} value={base.noShowPct} onChange={(e) => relax({ noShow: Number(e.target.value) })} />
          </label>
        </div>
        {relaxed && (
          <button type="button" className="pms-link" onClick={() => set({ relaxed: null })}>
            Revenir aux conditions de la politique
          </button>
        )}
        <p className="pms-hint">Les conditions choisies sont enregistrées avec la réservation et reprises dans la confirmation.</p>
      </details>
      {!transfer && (
        <p className="pms-call-warn">
          <strong>Jamais de numéro de carte</strong>, ni dans le PMS, ni sur un papier. Si le client veut le dicter : « Je préfère vous envoyer un lien
          sécurisé : vous saisirez votre carte vous-même. »
        </p>
      )}
    </section>
  );
}


// ── What to say: the key points, or the sentences ────────────────────────────

/** The desk's cue. By default the key points, on their own background so the eye
 *  finds them; « Texte à dire » shows the full sentences instead. */
function Cue({ text, points, full }: { text: string; points: readonly string[]; full: boolean }) {
  if (full) return <p className="pms-call-say">{text}</p>;
  return (
    <ul className="pms-call-points" aria-label="Points clés">
      {points.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  );
}

// ── An amount typed as text ──────────────────────────────────────────────────

/** A number input reformatted its own value to « 4.00 » on every keystroke, and
 *  a phone could not edit it any more. The text is the desk's while typing; the
 *  amount follows once it reads as one (« 4 », « 4,5 », « 1 250,00 »). */
function AmountInput({
  cents,
  onChange,
  placeholder,
  label,
}: {
  cents: number | null;
  onChange: (cents: number | null) => void;
  placeholder: string;
  label: string;
}) {
  const [text, setText] = useState(cents === null ? '' : amt(cents));
  const sent = useRef<number | null>(cents);
  // A value changed from elsewhere (« Recommencer », a new option) replaces the text.
  useEffect(() => {
    if (cents !== sent.current) {
      sent.current = cents;
      setText(cents === null ? '' : amt(cents));
    }
  }, [cents]);
  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={text}
      placeholder={placeholder}
      onChange={(e) => {
        setText(e.target.value);
        const c = e.target.value.trim() === '' ? null : parseEurosToCents(e.target.value);
        if (c === null && e.target.value.trim() !== '') return;
        sent.current = c;
        onChange(c);
      }}
    />
  );
}

// ── Beds, then the room itself ───────────────────────────────────────────────

/** The configurations the category's rooms really take, each with how many of
 *  those rooms are free over the stay; once one is chosen, those free rooms,
 *  drawn, to give one (operator, 2026-10-27 — the « préférences » rows went:
 *  the desk sees the rooms and picks). */
function RoomChoices({
  call,
  set,
  hostLabel,
  rooms,
  bedConfigs,
  people,
  free,
}: {
  call: CallDraft;
  set: (p: Partial<CallDraft>) => void;
  hostLabel: string;
  rooms: readonly Room[];
  bedConfigs: readonly BedConfig[];
  people: number;
  /** Free rooms over the stay, or null while unread. */
  free: ReadonlySet<number> | null;
}) {
  const beds = bedChoices(rooms, bedConfigs, people);
  const freeFitting = (bed: string | null) => (free === null ? null : roomsFitting(rooms, bed).filter((r) => free.has(r.id)));
  const countLabel = (n: number | null) => (n === null ? '' : n === 0 ? ' · aucune libre' : ` · ${n} libre${n > 1 ? 's' : ''}`);
  const layoutOf = (code: string) => bedConfigs.find((b) => b.code === code)?.layout ?? null;
  if (rooms.length === 0) return <p className="pms-hint">Aucune chambre rattachée à cette catégorie : lits à noter dans la note.</p>;
  const listed = call.bed === null ? null : freeFitting(call.bed);
  return (
    <>
      <fieldset className="pms-call-group">
        <legend>Lits — chambres {hostLabel.toLowerCase()}</legend>
        {beds.length === 0 && <p className="pms-hint">Aucune configuration de lits connue pour ces chambres.</p>}
        {beds.map((b) => (
          <button
            key={b.code}
            type="button"
            className={`pms-chip ${call.bed === b.code ? 'is-active' : ''}`}
            aria-pressed={call.bed === b.code}
            // Another configuration: the room given may not take it.
            onClick={() => set({ bed: call.bed === b.code ? null : b.code, roomId: null })}
          >
            {b.labelFr}
            {countLabel(freeFitting(b.code)?.length ?? null)}
          </button>
        ))}
      </fieldset>
      {listed !== null && (
        <fieldset className="pms-call-group">
          <legend>Chambres libres sur tout le séjour</legend>
          {listed.length === 0 && (
            <p className="pms-call-warn">
              Aucune chambre {hostLabel.toLowerCase()} ainsi faite n’est libre sur tout le séjour. À voir au planning avant de le promettre — ou proposer
              autre chose.
            </p>
          )}
          <div className="pms-call-units">
            {listed.map((r) => {
              const facts = roomFacts(r);
              const on = call.roomId === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  className={`pms-call-unit ${on ? 'is-active' : ''}`}
                  aria-pressed={on}
                  aria-label={`Chambre ${r.roomNumber}${facts.length ? `, ${facts.map((f) => f.label).join(', ')}` : ''}`}
                  onClick={() => set({ roomId: on ? null : r.id })}
                >
                  <strong>{r.roomNumber}</strong>
                  <BedIcons
                    layouts={r.configOptions.map((code) => ({ code, layout: layoutOf(code) }))}
                    doubleBedType={r.doubleBedType}
                    current={call.bed}
                  />
                  <RoomGlyphs room={r} />
                </button>
              );
            })}
          </div>
          <p className="pms-hint">
            {call.roomId === null
              ? 'Touchez une chambre pour l’attribuer — sinon elle le sera au planning.'
              : 'Attribuée à l’enregistrement : l’historique de la réservation garde la date et qui l’a donnée.'}
          </p>
        </fieldset>
      )}
      {free === null && <p className="pms-hint">Disponibilité des chambres non lue : le lit est noté, la chambre se donne au planning.</p>}
    </>
  );
}

/** What a room is beyond its beds, drawn as « Configuration » draws it — the
 *  view, the bath, the WC — and ♿ for a PMR room (operator, 2026-10-28). */
function RoomGlyphs({ room }: { room: Room }) {
  const pmr = roomFacts(room).some((f) => f.key === 'pmr');
  return (
    <span className="pms-call-unit-facts">
      <OutlookIcon kind={outlookKind(room.isSuperior, room.view)} isSuperior={room.isSuperior} view={room.view} />
      <BathIcon hasShower={room.hasShower} />
      <WcIcon notSeparated={room.wcNotSeparated} />
      {pmr && (
        <span title="accessible (PMR)" aria-label="accessible (PMR)">
          ♿
        </span>
      )}
    </span>
  );
}

/** CC2 — the grid restrictions a desk sale breaks, as the desk reads them
 *  (labels from GLOSSARY). */
function restrictionsFr(r: readonly StayRestriction[], maxStay?: number): string {
  return r
    .map((x) =>
      x === 'closed_to_arrival'
        ? 'Arrivée interdite'
        : x === 'closed_to_departure'
          ? 'Départ interdit'
          : `Séjour max. ${maxStay ?? ''} nuit${(maxStay ?? 0) > 1 ? 's' : ''}`,
    )
    .join(' · ');
}
